import { useState } from 'react';

import apiToken, { handleApiError } from '@/shared/services/axiosInstance';
import { formatPrice } from '@/shared/utils/priceUtils';
import { getCurrencySymbol } from '@/shared/utils/currencyUtils';
import { formatDateTimeKo } from '@/shared/utils/dateUtils';
import { useAuthGuard } from '@/shared/hooks/useAuthGuard';

import { OrderItem } from '../types/OrderTypes';

interface ShoppingDetailProps {
  uuid?: string;
}

const ShoppingDetail = ({ uuid }: ShoppingDetailProps) => {
  const [orderDetail, setOrderDetail] = useState<OrderItem | null>(null);

  const getOrderDetail = async () => {
    try {
      const response = await apiToken.get<{
        success: boolean;
        message: string;
        statusCode: number;
        data: OrderItem;
      }>(`/api/v1/order/detail/${uuid}`);
      setOrderDetail(response.data.data);
    } catch (error) {
      const errorMessage = handleApiError(error);
      console.error('Failed to fetch order detail:', errorMessage);
      alert('Failed to load the order details. Please try again in a moment.');
    }
  };

  const { isAuthenticated } = useAuthGuard({
    redirectTo: '/login',
    message: 'Please log in to access Order Detail.',
    onAuthenticated: getOrderDetail,
  });

  if (!orderDetail) {
    return <div>Loading...</div>;
  }

  return (
    <div>
      {isAuthenticated && (
        <section className="mx-auto">
          <div className="w-full max-w-[48rem]">
            <h2 className="text-center text-[1.25rem] font-black text-[#95699f]">
              Order Detail
            </h2>
            <hr className="mx-auto my-6 mb-8 border border-[#ccc]" />

            <div className="mx-auto mb-6 w-[720px] rounded-xl bg-white p-6 shadow-[7px_8px_14px_#22222208]">
              <div className="mb-6">
                <h6 className="mb-2 text-[14px] font-normal text-[#666]">
                  Order Number
                </h6>
                <p className="text-[16px] font-medium text-[#444]">
                  {orderDetail.orderNumber}
                </p>
              </div>
              <div className="mb-6">
                <h6 className="mb-2 text-[14px] font-normal text-[#666]">
                  Order Date
                </h6>
                <p className="text-[16px] font-medium text-[#444]">
                  {formatDateTimeKo(orderDetail.createdAt)}
                </p>
              </div>
              <div className="mb-6">
                <h6 className="mb-2 text-[14px] font-normal text-[#666]">
                  Order Status
                </h6>
                <p className="text-[16px] font-medium text-[#444]">
                  {orderDetail.status}
                </p>
              </div>
            </div>

            <div className="mx-auto w-[720px] rounded-xl bg-white p-6 shadow-[7px_8px_14px_#22222208]">
              <h2 className="mb-5 text-[18px] font-bold text-[#444]">
                Product Information
              </h2>
              <div className="flex flex-col gap-4">
                {orderDetail.orderDetails.map((detail) => {
                  const thumbnailUrl =
                    detail.productOption.thumbnailUrlArray[0] ||
                    './images/head.jpg';
                  const optionText = detail.productOption.optionNameValue2
                    ? `${detail.productOption.optionNameValue1} / ${detail.productOption.optionNameValue2}`
                    : detail.productOption.optionNameValue1;

                  return (
                    <div
                      key={detail.orderDetailUuid}
                      className="flex gap-4 rounded-lg border border-[#ddd] p-4"
                    >
                      <img
                        src={thumbnailUrl}
                        alt="product"
                        className="h-24 w-24 rounded object-cover"
                      />
                      <div className="mb-1 mt-2 w-full">
                        <h4 className="mb-2 text-[16px] font-semibold text-[#444]">
                          {detail.productOption.productTitle}
                        </h4>
                        <p className="mb-2 text-[12px] font-normal text-[#666]">
                          {/* Vendor information would need to be added to OrderDetail type */}
                        </p>
                        <p className="mb-4 text-[14px] font-normal text-[#95699f]">
                          option: {optionText}
                        </p>
                        <div className="flex items-center justify-between">
                          <p className="text-[12px] font-normal text-[#666]">
                            Quantity: {detail.quantity}
                          </p>
                          <span className="text-[14px] font-bold leading-[1.4] text-[#444]">
                            {formatPrice(detail.amount, detail.currencyCode)}{' '}
                            {getCurrencySymbol(detail.currencyCode)}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
                {orderDetail.orderMemos.length !== 0 && (
                  <>
                    <div className="mt-3 flex items-center justify-between rounded-lg bg-[#f4f4f4] p-5 text-[16px] font-semibold">
                      {' '}
                      <p className="text-[#444]">SubTotal</p>
                      <p className="text-[#444]">
                        {formatPrice(
                          orderDetail.beforeDiscountProductAmount,
                          orderDetail.currencyCode,
                        )}
                        {getCurrencySymbol(orderDetail.currencyCode)}{' '}
                      </p>
                    </div>
                    <div className="mt-3 flex flex-col items-start justify-between rounded-lg bg-[#f4f4f4] p-5 text-[16px] font-semibold">
                      {orderDetail.orderMemos.map((memo) => {
                        const parts = memo.content.split('/');
                        if (parts.length === 2) {
                          return (
                            <div
                              key={memo.orderMemoUuid}
                              className="flex w-full justify-between text-[#444]"
                            >
                              <span>ㄴ{parts[0].trim()}</span>
                              <span>{parts[1].trim()}</span>
                            </div>
                          );
                        }
                        return (
                          <p key={memo.orderMemoUuid} className="text-[#444]">
                            ㄴ{memo.content}
                          </p>
                        );
                      })}
                    </div>
                  </>
                )}

                <div className="mt-3 flex items-center justify-between rounded-lg bg-[#f4f4f4] p-5 text-[16px] font-semibold">
                  <p className="text-[#444]">Total</p>
                  <p className="text-[#95699f]">
                    {formatPrice(
                      orderDetail.finalPaymentAmount,
                      orderDetail.currencyCode,
                    )}{' '}
                    {getCurrencySymbol(orderDetail.currencyCode)}
                  </p>
                </div>
              </div>
            </div>

            {/* Shipping Information */}
            <div className="mx-auto mt-6 w-[720px] rounded-xl bg-white p-6 shadow-[7px_8px_14px_#22222208]">
              <h2 className="mb-5 text-[18px] font-bold text-[#444]">
                Shipping Information
              </h2>
              <div className="space-y-4">
                <div>
                  <h6 className="mb-2 text-[14px] font-normal text-[#666]">
                    Receiver Name
                  </h6>
                  <p className="text-[16px] font-medium text-[#444]">
                    {orderDetail.receiverInfo.receiverName}
                  </p>
                </div>
                <div>
                  <h6 className="mb-2 text-[14px] font-normal text-[#666]">
                    Company
                  </h6>
                  <p className="text-[16px] font-medium text-[#444]">
                    {orderDetail.receiverInfo.companyName}
                  </p>
                </div>
                <div>
                  <h6 className="mb-2 text-[14px] font-normal text-[#666]">
                    Phone Number
                  </h6>
                  <p className="text-[16px] font-medium text-[#444]">
                    {orderDetail.receiverInfo.phoneNumber}
                  </p>
                </div>
                <div>
                  <h6 className="mb-2 text-[14px] font-normal text-[#666]">
                    Address
                  </h6>
                  <p className="text-[16px] font-medium text-[#444]">
                    {orderDetail.receiverInfo.addressDetail}
                    {orderDetail.receiverInfo.postalCode &&
                      ` (${orderDetail.receiverInfo.postalCode})`}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default ShoppingDetail;
